import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './EmailVerification.module.css';
import { Mail, ArrowLeft, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import { verifyEmail, resendVerificationCode, completeRegistration } from '../../Fetch/fetchUser';

export default function EmailVerification({ 
    email: propEmail, 
    onVerificationComplete, 
    onBack, 
    setCurrentPage,
    fromForgotPassword: propFromForgotPassword = false  // הוספתי prop חדש
}) {
    const [code, setCode] = useState(['', '', '', '', '', '']);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);
    const [isCompleting, setIsCompleting] = useState(false);
    
    const inputRefs = useRef([]);
    const navigate = useNavigate();
    const location = useLocation();

    // Get email and other data from navigation state or props
    const email = location.state?.email || propEmail;
    const fromForgotPassword = location.state?.fromForgotPassword || propFromForgotPassword;  // שימוש גם ב-prop
    const returnTo = location.state?.returnTo;

    // Timer for resend button
    useEffect(() => {
        let timer;
        if (timeLeft > 0) {
            timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [timeLeft]);

    // Initialize refs
    useEffect(() => {
        inputRefs.current = inputRefs.current.slice(0, 6);
    }, []);

    const handleCodeChange = (index, value) => {
        // Only allow digits
        if (!/^\d*$/.test(value)) return;

        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);
        setError('');

        // Auto-focus next input
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }

        // Auto-submit when all 6 digits are entered
        if (newCode.every(digit => digit !== '') && newCode.join('').length === 6) {
            handleVerification(newCode.join(''));
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text/plain');
        const digits = pastedData.replace(/\D/g, '').slice(0, 6);
        
        if (digits.length > 0) {
            const newCode = [...code];
            for (let i = 0; i < digits.length && i < 6; i++) {
                newCode[i] = digits[i];
            }
            setCode(newCode);
            
            // Focus appropriate input
            const nextEmptyIndex = newCode.findIndex(digit => digit === '');
            if (nextEmptyIndex !== -1) {
                inputRefs.current[nextEmptyIndex]?.focus();
            } else {
                inputRefs.current[5]?.focus();
                if (newCode.every(digit => digit !== '')) {
                    handleVerification(newCode.join(''));
                }
            }
        }
    };

    const handleVerification = async (verificationCode = null) => {
        const codeToVerify = verificationCode || code.join('');
        
        if (codeToVerify.length !== 6) {
            setError('אנא הזן קוד בן 6 ספרות');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            console.log('🔍 Verification Debug:', {
                email: email,
                code: codeToVerify,
                fromForgotPassword: fromForgotPassword,
                locationState: location.state
            });

            // If coming from forgot password, use special email verification endpoint
            if (fromForgotPassword) {
                
                // Use special endpoint for email verification from forgot password
                const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://localhost:7204'}/api/auth/verify-email-for-reset`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        email: email,
                        code: codeToVerify
                    }),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || 'אימות נכשל');
                }

                setSuccess('האימות הושלם בהצלחה!');
                
                setTimeout(() => {
                    if (onVerificationComplete) {
                        // Use the provided callback function
                        onVerificationComplete();
                    } else if (returnTo === 'forgot-password') {
                        // Go back to forgot password with success message
                        navigate('/forgot-password', {
                            state: { 
                                email: email,
                                message: 'כתובת המייל אומתה בהצלחה! כעת תוכל לאפס את הסיסמה.'
                            }
                        });
                    } else {
                        // Fallback - go back to forgot password
                        navigate('/forgot-password', {
                            state: { 
                                email: email,
                                message: 'כתובת המייל אומתה בהצלחה! כעת תוכל לאפס את הסיסמה.'
                            }
                        });
                    }
                }, 2000);
            } else {
                // Normal email verification - use regular verifyEmail
                const verificationResult = await verifyEmail(email, codeToVerify);
                
                // Check if this is a new registration that needs completion
                // or an existing user just verifying their email
                const pendingVerificationData = localStorage.getItem('pendingVerification');
                let isNewRegistration = false;
                let fromLogin = false;
                
                try {
                    if (pendingVerificationData) {
                        const pendingData = JSON.parse(pendingVerificationData);
                        fromLogin = pendingData.fromLogin === true;
                        console.log('🔍 Pending verification data:', pendingData);
                        // If there's pending data and it's not from login, it's a new registration
                        isNewRegistration = !fromLogin;
                    } else {
                        // No pending data means it's an existing user
                        isNewRegistration = false;
                    }
                } catch {
                    console.log('🔍 Could not parse pending verification data, treating as existing user');
                    isNewRegistration = false;
                }
                
                console.log('🔍 Registration flow decision:', { 
                    isNewRegistration, 
                    fromLogin, 
                    hasPendingData: !!pendingVerificationData 
                });
                
                if (isNewRegistration) {
                    // This is a new registration - complete the registration
                    setIsCompleting(true);
                    try {
                        const result = await completeRegistration(email);
                        setSuccess('האימות הושלם בהצלחה! מעביר אותך לדף ההתחברות...');
                        
                        // Store success message and redirect to login
                        setTimeout(() => {
                            if (onVerificationComplete) {
                                onVerificationComplete(result);
                            } else {
                                setCurrentPage ? setCurrentPage('login') : navigate('/login');
                            }
                        }, 2000);
                    } catch (registrationError) {
                        console.warn('Complete registration failed, but verification succeeded. User might already exist.');
                        // Fall back to treating as existing user verification
                        setSuccess('האימות הושלם בהצלחה! מעביר אותך לדף ההתחברות...');
                        localStorage.setItem('registrationSuccess', 'כתובת המייל אומתה בהצלחה!');
                        
                        setTimeout(() => {
                            if (onVerificationComplete) {
                                onVerificationComplete(verificationResult);
                            } else {
                                setCurrentPage ? setCurrentPage('login') : navigate('/login');
                            }
                        }, 2000);
                    }
                } else {
                    // This is an existing user just verifying email - skip complete registration
                    setSuccess('האימות הושלם בהצלחה! מעביר אותך לדף ההתחברות...');
                    localStorage.setItem('registrationSuccess', 'כתובת המייל אומתה בהצלחה!');
                    
                    // Clear the pending verification data
                    localStorage.removeItem('pendingVerification');
                    
                    setTimeout(() => {
                        if (onVerificationComplete) {
                            onVerificationComplete(verificationResult);
                        } else {
                            setCurrentPage ? setCurrentPage('login') : navigate('/login');
                        }
                    }, 2000);
                }
            }

        } catch (error) {
            setError(error.message || 'אימות נכשל. אנא נסה שוב.');
            setCode(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
        } finally {
            setIsLoading(false);
            setIsCompleting(false);
        }
    };

    const handleResendCode = async () => {
        setIsResending(true);
        setError('');
        setSuccess('');

        try {
            await resendVerificationCode(email);
            setSuccess('קוד חדש נשלח לכתובת המייל שלך');
            setTimeLeft(60); // 60 seconds cooldown
            setCode(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
        } catch (error) {
            setError(error.message || 'שגיאה בשליחת קוד חדש');
        } finally {
            setIsResending(false);
        }
    };

    const handleGoBack = () => {
        if (fromForgotPassword && onBack) {
            // Use provided onBack function for ForgotPassword
            onBack();
        } else if (fromForgotPassword) {
            // Fallback for ForgotPassword - go back to forgot password
            navigate('/forgot-password', {
                state: { email: email }
            });
        } else if (onBack) {
            // Use provided onBack function
            onBack();
        } else {
            // Default navigation
            navigate(-1);
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Show error if no email is provided
    if (!email) {
        return (
            <div className={styles.verificationContainer} dir="rtl">
                <div className={styles.verificationCard}>
                    <div className={`${styles.message} ${styles.error}`}>
                        <AlertCircle size={20} />
                        <span>כתובת מייל לא נמצאה. אנא חזור לדף הקודם.</span>
                    </div>
                    <button
                        onClick={() => navigate('/login')}
                        className={styles.verifyButton}
                    >
                        חזור לדף ההתחברות
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.verificationContainer} dir="rtl">
            <div className={styles.verificationCard}>
                {/* Back Button */}
                <button
                    onClick={handleGoBack}
                    className={styles.backButton}
                    disabled={isLoading || isCompleting}
                >
                    <ArrowLeft size={20} />
                    חזור
                </button>

                {/* Header */}
                <div className={styles.header}>
                    <div className={styles.iconContainer}>
                        <Mail className={styles.mailIcon} />
                    </div>
                    <h1 className={styles.title}>
                        {fromForgotPassword ? 'אימות כתובת מייל לאיפוס סיסמה' : 'אימות כתובת מייל'}
                    </h1>
                    <p className={styles.subtitle}>
                        שלחנו קוד אימות בן 6 ספרות לכתובת
                    </p>
                    <p className={styles.email}>{email}</p>
                </div>

                {/* Messages */}
                {error && (
                    <div className={`${styles.message} ${styles.error}`}>
                        <AlertCircle size={20} />
                        <span>{error}</span>
                    </div>
                )}

                {success && (
                    <div className={`${styles.message} ${styles.success}`}>
                        <CheckCircle size={20} />
                        <span>{success}</span>
                    </div>
                )}

                {/* Code Input */}
                <div className={styles.codeContainer}>
                    <label className={styles.codeLabel}>הזן קוד אימות</label>
                    <div className={styles.codeInputs}>
                        {code.map((digit, index) => (
                            <input
                                key={index}
                                ref={el => inputRefs.current[index] = el}
                                type="text"
                                maxLength={1}
                                value={digit}
                                onChange={(e) => handleCodeChange(index, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(index, e)}
                                onPaste={handlePaste}
                                className={`${styles.codeInput} ${error ? styles.inputError : ''}`}
                                disabled={isLoading || isCompleting}
                                autoComplete="off"
                                inputMode="numeric"
                            />
                        ))}
                    </div>
                </div>

                {/* Loading States */}
                {isLoading && (
                    <div className={styles.loadingMessage}>
                        מאמת קוד...
                    </div>
                )}

                {isCompleting && !fromForgotPassword && (
                    <div className={styles.loadingMessage}>
                        משלים רישום...
                    </div>
                )}

                {/* Verify Button */}
                <button
                    onClick={() => handleVerification()}
                    className={`${styles.verifyButton} ${
                        (isLoading || isCompleting || code.some(digit => digit === '')) 
                            ? styles.disabled 
                            : ''
                    }`}
                    disabled={isLoading || isCompleting || code.some(digit => digit === '')}
                >
                    {isLoading || isCompleting ? 'מעבד...' : 'אמת קוד'}
                </button>

                {/* Resend Section */}
                <div className={styles.resendSection}>
                    <p className={styles.resendText}>לא קיבלת קוד?</p>
                    {timeLeft > 0 ? (
                        <p className={styles.cooldownText}>
                            ניתן לשלוח קוד חדש בעוד {formatTime(timeLeft)}
                        </p>
                    ) : (
                        <button
                            onClick={handleResendCode}
                            className={styles.resendButton}
                            disabled={isResending || isLoading || isCompleting}
                        >
                            <RefreshCw 
                                size={16} 
                                className={isResending ? styles.spinning : ''} 
                            />
                            {isResending ? 'שולח...' : 'שלח קוד חדש'}
                        </button>
                    )}
                </div>

                {/* Instructions */}
                <div className={styles.instructions}>
                    <h3>טיפים:</h3>
                    <ul>
                        <li>בדוק את תיבת הספאם אם אינך רואה את המייל</li>
                        <li>הקוד תקף ל-15 דקות</li>
                        <li>ניתן להדביק את הקוד מהמייל</li>
                        {fromForgotPassword && (
                            <li>לאחר האימות תוכל לאפס את הסיסמה שלך</li>
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
}